import { pprops } from '@frontend/sports/common/core/utils/redux';
import { createAction, props } from '@ngrx/store';

import { NumpadAction } from '../../../lib/numpad/model';
import { PickId } from '../../core/picks/pick-id';
import { SlipId } from '../bet-generation/models';
import { FinishPlaceBetPayload, QueryPlaceStatusPayload } from '../betplacement/actions';
import { BetPlacementError } from '../validation/errors/bet-placement-error';
import { BetslipError } from '../validation/errors/betslip-error';
import { BetslipTypePickErrors } from '../validation/state';
import { selectTrackingDetails } from './quick-bet.selectors';
import { IQuickBetPlaceResultState, IQuickBetStakeState, QuickBetActionButtonState } from './quick-bet.state';

export interface IShowPickChangedWarningPayload {
    pickLocked: boolean;
    oddsChanged: boolean;
    isAcceptAndPlaceBetEnabled: boolean;
}

export interface IPlaceBetPayload {
    requestId: string;
}

export interface IClickPayload {
    requestId: string;
    state: QuickBetActionButtonState;
}

export interface IHasClosedPickPayload {
    hasClosedPick: boolean;
}

export interface IHasLockedPickPayload {
    hasLockedPick: boolean;
}

export interface ISportcastOption {
    name: string;
    longId: string;
}

export class QuickBetActions {
    static readonly ACTION_SCHEMA = '@quick-bet';

    private static readonly ADD_PICK = `${QuickBetActions.ACTION_SCHEMA}/ADD_PICK`;
    private static readonly REMOVE_PICK_BUTTON_CLICK = `${QuickBetActions.ACTION_SCHEMA}/REMOVE_PICK_BUTTON_CLICK`;
    private static readonly REMOVE_PICK = `${QuickBetActions.ACTION_SCHEMA}/REMOVE_PICK`;
    private static readonly REQUEST_REMOVE_MULTIPLE_PICKS = `${QuickBetActions.ACTION_SCHEMA}/REQUEST_REMOVE_MULTIPLE_PICKS`;
    private static readonly REMOVE_MULTIPLE_PICKS = `${QuickBetActions.ACTION_SCHEMA}/REMOVE_MULTIPLE_PICKS`;
    private static readonly INIT = `${QuickBetActions.ACTION_SCHEMA}/INIT`;
    private static readonly OPEN_QUICK_BET = `${QuickBetActions.ACTION_SCHEMA}/OPEN_QUICK_BET`;
    private static readonly CLOSE_QUICK_BET = `${QuickBetActions.ACTION_SCHEMA}/CLOSE_QUICK_BET`;
    private static readonly SHOW_QUICK_BET = `${QuickBetActions.ACTION_SCHEMA}/SHOW_QUICK_BET`;
    private static readonly HIDE_QUICK_BET = `${QuickBetActions.ACTION_SCHEMA}/HIDE_QUICK_BET`;
    private static readonly OPEN_CONFIRMATION = `${QuickBetActions.ACTION_SCHEMA}/OPEN_CONFIRMATION`;
    private static readonly CLOSE_CONFIRMATION = `${QuickBetActions.ACTION_SCHEMA}/CLOSE_CONFIRMATION`;
    private static readonly UPDATE_PLACE_BET_RESULT = `${QuickBetActions.ACTION_SCHEMA}/UPDATE_PLACE_BET_RESULT`;
    private static readonly CLOSE_QUICK_BET_UI = `${QuickBetActions.ACTION_SCHEMA}/CLOSE_QUICK_BET_UI`;
    private static readonly CLEAN_QUICK_BET_AFTER_CLOSE_UI = `${QuickBetActions.ACTION_SCHEMA}/CLEAN_QUICK_BET_AFTER_CLOSE_UI`;
    private static readonly CLOSE_QUICK_BET_UI_OVERLAY = `${QuickBetActions.ACTION_SCHEMA}/CLOSE_QUICK_BET_UI_OVERLAY`;
    private static readonly CHANGE_STAKE = `${QuickBetActions.ACTION_SCHEMA}/CHANGE_STAKE`;
    private static readonly UPDATE_STAKE = `${QuickBetActions.ACTION_SCHEMA}/UPDATE_STAKE`;
    private static readonly SET_STAKE_STATE = `${QuickBetActions.ACTION_SCHEMA}/SET_STAKE_STATE`;
    private static readonly NUMPAD_ACTION = `${QuickBetActions.ACTION_SCHEMA}/NUMPAD_ACTION`;
    private static readonly RESET_RESULT_STATE = '@betslip/quick-bet/RESET_RESULT_STATE';
    private static readonly RESET_UI_STATE = '@betslip/quick-bet/RESET_UI_STATE';
    private static readonly SET_ACTION_BUTTON = `${QuickBetActions.ACTION_SCHEMA}/SET_ACTION_BUTTON`;
    private static readonly SET_HAS_CLOSED_PICK = `${QuickBetActions.ACTION_SCHEMA}/SET_HAS_CLOSED_PICK`;
    private static readonly SET_HAS_LOCKED_PICK = `${QuickBetActions.ACTION_SCHEMA}/SET_HAS_LOCKED_PICK`;
    private static readonly PLACE_BET = `${QuickBetActions.ACTION_SCHEMA}/PLACE_BET`;
    private static readonly REQUEST_PLACE_BET = `${QuickBetActions.ACTION_SCHEMA}/REQUEST_PLACE_BET`;
    private static readonly ACCEPT_ODDS_CHANGES = `${QuickBetActions.ACTION_SCHEMA}/ACCEPT_ODDS_CHANGES`;
    private static readonly ACCEPT_MARKET_CLOSED_CHANGES = `${QuickBetActions.ACTION_SCHEMA}/ACCEPT_MARKET_CLOSED_CHANGES`;
    private static readonly SHOW_TECHNICAL_ERROR_DIALOG = `${QuickBetActions.ACTION_SCHEMA}/SHOW_TECHNICAL_ERROR_DIALOG`;
    private static readonly SHOW_PLACE_BET_ERROR_DIALOG = `${QuickBetActions.ACTION_SCHEMA}/SHOW_PLACE_BET_ERROR_DIALOG`;
    private static readonly SHOW_PLACE_BET_WARNING = `${QuickBetActions.ACTION_SCHEMA}/SHOW_PLACE_BET_WARNING`;
    private static readonly SHOW_PICK_CHANGED_WARNING = `${QuickBetActions.ACTION_SCHEMA}/SHOW_PICK_CHANGED_WARNING`;
    private static readonly ACTION_BUTTON_CLICK = `${QuickBetActions.ACTION_SCHEMA}/ACTION_BUTTON_CLICK`;
    private static readonly KEEP_OPEN = `${QuickBetActions.ACTION_SCHEMA}/KEEP_OPEN`;
    private static readonly DEPOSIT = `${QuickBetActions.ACTION_SCHEMA}/DEPOSIT`;
    private static readonly MAKE_DEPOSIT = `${QuickBetActions.ACTION_SCHEMA}/MAKE_DEPOSIT`;
    private static readonly DEPOSIT_AND_PLACE = `${QuickBetActions.ACTION_SCHEMA}/DEPOSIT_AND_PLACE`;
    private static readonly VIEW_BONUS = `${QuickBetActions.ACTION_SCHEMA}/VIEW_BONUS`;
    private static readonly LOGIN = `${QuickBetActions.ACTION_SCHEMA}/LOGIN`;
    private static readonly SHOW_HELP = `${QuickBetActions.ACTION_SCHEMA}/SHOW_HELP`;
    private static readonly HIDE_HELP = `${QuickBetActions.ACTION_SCHEMA}/HIDE_HELP`;
    private static readonly HELP_CLOSE_CLICK = `${QuickBetActions.ACTION_SCHEMA}/HELP_CLOSE_CLICK`;
    private static readonly SHOW_WALLET_SELECTOR = `${QuickBetActions.ACTION_SCHEMA}/SHOW_WALLET_SELECTOR`;
    private static readonly CLEAR_WARNINGS = `${QuickBetActions.ACTION_SCHEMA}/CLEAR_WARNINGS`;
    private static readonly FINISH_PLACE_BET = `${QuickBetActions.ACTION_SCHEMA}/FINISH_PLACE_BET`;
    private static readonly TRACK_BET_PLACEMENT_RESULT = `${QuickBetActions.ACTION_SCHEMA}/TRACK_BET_PLACEMENT_RESULT`;
    private static readonly INIT_BET_PLACEMENT = `${QuickBetActions.ACTION_SCHEMA}/INIT_BET_PLACEMENT`;
    private static readonly FINALIZE_BET_PLACEMENT = `${QuickBetActions.ACTION_SCHEMA}/FINALIZE_BET_PLACEMENT`;
    private static readonly PLACE_BET_RESPONSE_ERRORS = `${QuickBetActions.ACTION_SCHEMA}/PLACE_BET_RESPONSE_ERRORS`;
    private static readonly SET_DISPLAY_HELPER_TO_DEFAULT = `${QuickBetActions.ACTION_SCHEMA}/SET_DISPLAY_HELPER_TO_DEFAULT`;
    private static readonly REPLACE_PICK = `${QuickBetActions.ACTION_SCHEMA}/REPLACE_PICK`;
    private static readonly QUERY_PLACE_STATUS = `${QuickBetActions.ACTION_SCHEMA}/QUERY_PLACE_STATUS`;
    private static readonly SET_HIDDEN_BY_ERROR = `${QuickBetActions.ACTION_SCHEMA}/SET_HIDDEN_BY_ERROR`;
    private static readonly GO_TO_MY_BETS = `${QuickBetActions.ACTION_SCHEMA}/GO_TO_MY_BETS`;
    private static readonly CLOSE_RESULT_SCREEN = `${QuickBetActions.ACTION_SCHEMA}/CLOSE_RESULT_SCREEN`;
    private static readonly OPEN_BETSLIP = `${QuickBetActions.ACTION_SCHEMA}/OPEN_BETSLIP`;
    private static readonly RESET_ADDED_TO_BETSLIP = `${QuickBetActions.ACTION_SCHEMA}/RESET_ADDED_TO_BETSLIP`;
    private static readonly RESET_TO_PLACE_STATE = `${QuickBetActions.ACTION_SCHEMA}/RESET_TO_PLACE_STATE`;

    /** Handle picks */

    static addPick = createAction(QuickBetActions.ADD_PICK, props<{ pickId: PickId }>());
    static removePick = createAction(QuickBetActions.REMOVE_PICK, props<{ cleanBetslip: boolean; pickId: PickId }>());
    static requestRemoveMultiplePicks = createAction(QuickBetActions.REQUEST_REMOVE_MULTIPLE_PICKS, props<{ cleanBetslip: boolean }>());
    static removeMultiplePicks = createAction(QuickBetActions.REMOVE_MULTIPLE_PICKS, props<{ pickIds: PickId[] }>());
    static replacePick = createAction(QuickBetActions.REPLACE_PICK, props<{ oldPickId: PickId; newPickId: PickId }>());
    static removePickButtonClick = createAction(
        QuickBetActions.REMOVE_PICK_BUTTON_CLICK,
        props<{ trackingDetails: ReturnType<typeof selectTrackingDetails> }>(),
    );

    /** Action buttons */

    static actionButtonClick = createAction(QuickBetActions.ACTION_BUTTON_CLICK, pprops<IClickPayload>());
    static setActionButton = createAction(QuickBetActions.SET_ACTION_BUTTON, pprops<QuickBetActionButtonState>());
    static makeDeposit = createAction(QuickBetActions.MAKE_DEPOSIT);
    static deposit = createAction(QuickBetActions.DEPOSIT);
    static depositAndBet = createAction(QuickBetActions.DEPOSIT_AND_PLACE, props<{ requestId: string }>());
    static viewBonus = createAction(QuickBetActions.VIEW_BONUS);
    static login = createAction(QuickBetActions.LOGIN);
    static acceptOddsChanges = createAction(QuickBetActions.ACCEPT_ODDS_CHANGES);
    static acceptMarketClosedChanges = createAction(QuickBetActions.ACCEPT_MARKET_CLOSED_CHANGES);
    static goToMyBets = createAction(QuickBetActions.GO_TO_MY_BETS, props<{ inPopup: boolean }>());

    /** UI state */

    static resetUiState = createAction(QuickBetActions.RESET_UI_STATE);
    static closeQuickbetUi = createAction(QuickBetActions.CLOSE_QUICK_BET_UI);
    static cleanQuickBetAfterCloseUI = createAction(QuickBetActions.CLEAN_QUICK_BET_AFTER_CLOSE_UI);
    static closeQuickBetUiOverlay = createAction(QuickBetActions.CLOSE_QUICK_BET_UI_OVERLAY, props<{ eventName: string | undefined }>());
    static closeQuickBet = createAction(QuickBetActions.CLOSE_QUICK_BET, props<{ cleanErrors?: boolean }>());
    static setDisplayHelperToDefault = createAction(QuickBetActions.SET_DISPLAY_HELPER_TO_DEFAULT);
    static init = createAction(QuickBetActions.INIT);
    static openQuickBet = createAction(QuickBetActions.OPEN_QUICK_BET);
    static showQuickBet = createAction(QuickBetActions.SHOW_QUICK_BET);
    static hideQuickBet = createAction(QuickBetActions.HIDE_QUICK_BET);
    static keepOpen = createAction(QuickBetActions.KEEP_OPEN, props<{ open: boolean }>());
    static showHelp = createAction(QuickBetActions.SHOW_HELP);
    static hideHelp = createAction(QuickBetActions.HIDE_HELP);
    static helpCloseClick = createAction(QuickBetActions.HELP_CLOSE_CLICK);
    static setHiddenByError = createAction(QuickBetActions.SET_HIDDEN_BY_ERROR, props<{ isHidden: boolean }>());
    static closeResultScreen = createAction(QuickBetActions.CLOSE_RESULT_SCREEN);

    /** Bet placement */

    static openConfirmation = createAction(QuickBetActions.OPEN_CONFIRMATION);
    static resetResultState = createAction(QuickBetActions.RESET_RESULT_STATE);
    static updatePlaceBetResult = createAction(QuickBetActions.UPDATE_PLACE_BET_RESULT, pprops<IQuickBetPlaceResultState>());
    static closeConfirmation = createAction(QuickBetActions.CLOSE_CONFIRMATION);
    static placeBet = createAction(QuickBetActions.PLACE_BET, pprops<IPlaceBetPayload>());
    static requestPlaceBet = createAction(QuickBetActions.REQUEST_PLACE_BET, pprops<IPlaceBetPayload>());
    static showPlaceBetErrorDialog = createAction(QuickBetActions.SHOW_PLACE_BET_ERROR_DIALOG);
    static showPlaceBetWarning = createAction(QuickBetActions.SHOW_PLACE_BET_WARNING);
    static finishPlaceBet = createAction(QuickBetActions.FINISH_PLACE_BET, props<FinishPlaceBetPayload>());
    static trackBetPlacementResult = createAction(QuickBetActions.TRACK_BET_PLACEMENT_RESULT, props<FinishPlaceBetPayload>());
    static initBetPlacement = createAction(QuickBetActions.INIT_BET_PLACEMENT);
    static finalizeBetPlacement = createAction(QuickBetActions.FINALIZE_BET_PLACEMENT);
    static queryPlaceStatus = createAction(QuickBetActions.QUERY_PLACE_STATUS, props<QueryPlaceStatusPayload & { balanceAtPlacement: number }>());
    static placeBetResponseErrors = createAction(
        QuickBetActions.PLACE_BET_RESPONSE_ERRORS,
        props<{ pickErrors: BetslipTypePickErrors; betslipErrors: BetslipError[]; slipErrors: Record<SlipId, BetPlacementError[]> }>(),
    );

    /** Stake handling */

    static changeStake = createAction(QuickBetActions.CHANGE_STAKE, props<{ stake: string; silent?: boolean }>());
    static updateStake = createAction(QuickBetActions.UPDATE_STAKE, props<{ stake: string; silent?: boolean }>());
    static setStakeState = createAction(QuickBetActions.SET_STAKE_STATE, props<IQuickBetStakeState>());
    static numpadAction = createAction(QuickBetActions.NUMPAD_ACTION, props<{ action: NumpadAction }>());
    static showWalletSelector = createAction(QuickBetActions.SHOW_WALLET_SELECTOR);

    /** Picks state */

    static setHasClosedPick = createAction(QuickBetActions.SET_HAS_CLOSED_PICK, pprops<IHasClosedPickPayload>());
    static setHasLockedPick = createAction(QuickBetActions.SET_HAS_LOCKED_PICK, pprops<IHasLockedPickPayload>());
    static showPickChangedWarning = createAction(QuickBetActions.SHOW_PICK_CHANGED_WARNING, pprops<IShowPickChangedWarningPayload>());
    static showTechnicalErrorDialog = createAction(QuickBetActions.SHOW_TECHNICAL_ERROR_DIALOG);
    static clearWarnings = createAction(QuickBetActions.CLEAR_WARNINGS);
    // fires when the betslip is open from QB(either "Add to Betslip" OR click on pick name)
    static openBetslip = createAction(QuickBetActions.OPEN_BETSLIP);
    // fires when we want to clean-up the state when returning from Betslip to QB, when QB was open before opening the betslip
    static resetAddedToBetslip = createAction(QuickBetActions.RESET_ADDED_TO_BETSLIP);
    // resets from Success state to Place state
    static resetToPlaceState = createAction(QuickBetActions.RESET_TO_PLACE_STATE);
}
